/* eslint-disable no-nested-ternary */
import React, { useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import Webcam from 'react-webcam';
import ReactPlayer from 'react-player';

import Spinner from '../../../../shared/_TBComponents/Spinner';
import NoHoverButton from '../../../../shared/components/NoHoverButton';

import { setSubActiveReduxModal } from '../../ducks/feedActions';

const SpinnerWrapper = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 700px;
  height: 395px;
`;

const WebcamContainer = styled('div')`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  .text {
    font-size: 26px;
    font-weight: 300;
  }
  .capture-button {
    width: 180px;
    background-color: ${(props) => props.theme.colors.green};
    color: #ffffff;

    &:hover {
      opacity: 0.7;
    }
  }
`;

const videoConstraints = {
  width: 1280,
  height: 720,
  facingMode: 'user',
};

const WebcamCapture = ({ handleUploadMedia }) => {
  const dispatch = useDispatch();
  const webcamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const [capturing, setCapturing] = useState(false);
  const [isCaptured, setIsCaptured] = useState(false);

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const uploadMedia = useSelector((state) => state.feed.data.uploadMedia);

  const handleDataAvailable = ({ data }) => {
    if (data.size > 0) {
      setIsCaptured(true);
      setCapturing(false);
      const file = new File([data], 'mp4', {
        type: 'video/mp4',
      });
      const fileArray = [file];
      const type = fileArray[0].type.split('/').shift();
      handleUploadMedia({
        accountCode: currentUser.accountCode,
        acceptedFile: fileArray,
        type,
      });
    }
  };

  const handleStartCaptureClick = React.useCallback(() => {
    setCapturing(true);
    mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream);
    mediaRecorderRef.current.addEventListener(
      'dataavailable',
      handleDataAvailable,
    );
    mediaRecorderRef.current.start();
  }, [webcamRef, setCapturing, mediaRecorderRef]);

  const handleStopCaptureClick = React.useCallback(() => {
    mediaRecorderRef.current.stop();
  }, [mediaRecorderRef, webcamRef, setCapturing]);

  const handleDoneClick = () => {
    dispatch(setSubActiveReduxModal(''));
  };

  const renderButton = () => {
    const baseProps = {
      className: 'modal-button',
      customColor: '#10cd8c',
      noHover: true,
      noBorder: true,
      bottom: true,
      fullWidth: true,
      large: true,
      primary: true,
      square: true,
    };
    let buttonConfig = {};
    if (isCaptured) {
      buttonConfig = { cta: 'Done', onClick: handleDoneClick, 'aria-label': 'Done' };
    } else if (capturing) {
      buttonConfig = { cta: 'Stop Capture', onClick: handleStopCaptureClick, 'aria-label': 'Stop recording' };
    } else {
      buttonConfig = { cta: 'Start Capture', onClick: handleStartCaptureClick, 'aria-label': 'Start recording' };
    }

    return <NoHoverButton {...baseProps} {...buttonConfig} />;
  };

  const renderPlayer = () => {
    if (isCaptured) {
      if (uploadMedia && uploadMedia.mediaUrl) {
        return (
          <ReactPlayer
            url={uploadMedia.mediaUrl}
            controls
            width={700}
            height={395}
          />
        );
      }
      return (
        <SpinnerWrapper>
          <Spinner />
        </SpinnerWrapper>
      );
    }
    return (
      <Webcam
        width={700}
        height={395}
        ref={webcamRef}
        videoConstraints={videoConstraints}
      />
    );
  };

  return (
    <WebcamContainer>
      {renderPlayer()}
      {renderButton()}
    </WebcamContainer>
  );
};

WebcamCapture.propTypes = {
  handleUploadMedia: PropTypes.func.isRequired,
};

export default WebcamCapture;
